import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ServiceStatus = () => {
  const [systemStatus, setSystemStatus] = useState('operational');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Simulate system status check
  useEffect(() => {
    const checkSystemStatus = () => {
      const statuses = ['operational', 'operational', 'operational', 'maintenance'];
      const randomStatus = statuses?.[Math.floor(Math.random() * statuses?.length)];
      setSystemStatus(randomStatus);
      setLastUpdated(new Date());
    };

    checkSystemStatus();
    const interval = setInterval(checkSystemStatus, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const getStatusConfig = () => {
    switch (systemStatus) {
      case 'operational':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          dotColor: 'bg-success',
          text: 'ระบบทำงานปกติ',
          description: 'บริการทั้งหมดพร้อมให้บริการ',
          icon: 'CheckCircle'
        };
      case 'maintenance':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          dotColor: 'bg-warning',
          text: 'ปรับปรุงระบบ',
          description: 'บางบริการอาจไม่พร้อมใช้งานชั่วคราว',
          icon: 'Settings'
        };
      case 'outage':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          dotColor: 'bg-error',
          text: 'ระบบขัดข้อง',
          description: 'ระบบไม่สามารถใช้งานได้ชั่วคราว',
          icon: 'AlertTriangle'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          dotColor: 'bg-muted-foreground',
          text: 'ไม่ทราบสถานะ',
          description: 'ไม่สามารถตรวจสอบสถานะระบบได้',
          icon: 'HelpCircle'
        };
    }
  };

  const statusConfig = getStatusConfig();

  const formatTime = (date) => {
    return date?.toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const announcements = [
    {
      id: 1,
      type: 'info',
      title: 'ปรับปรุงระบบในช่วงสุดสัปดาห์',
      message: 'ระบบจะมีการปรับปรุงในวันเสาร์ที่ 14 กันยายน 2567 เวลา 02:00-06:00 น.',
      date: new Date('2024-09-12')
    },
    {
      id: 2,
      type: 'success',
      title: 'เพิ่มช่องทางการติดต่อใหม่',
      message: 'เปิดให้บริการ LINE Official Account @DistrictOffice สำหรับสอบถามข้อมูล',
      date: new Date('2024-09-10')
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-government">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Activity" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-foreground">
          สถานะระบบ
        </h3>
      </div>
      {/* Current Status */}
      <div className={`${statusConfig?.bgColor} rounded-lg p-4 mb-6`}>
        <div className="flex items-center space-x-3 mb-2">
          <div className={`w-3 h-3 rounded-full ${statusConfig?.dotColor} animate-pulse`} />
          <Icon name={statusConfig?.icon} size={20} className={statusConfig?.color} />
          <span className={`font-semibold ${statusConfig?.color}`}>
            {statusConfig?.text}
          </span>
        </div>
        <p className="text-sm text-muted-foreground ml-6">
          {statusConfig?.description}
        </p>
        <div className="flex items-center space-x-2 mt-3 ml-6">
          <Icon name="Clock" size={14} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            อัปเดตล่าสุด: {formatTime(lastUpdated)}
          </span>
        </div>
      </div>
      {/* Service Announcements */}
      <div className="space-y-4">
        <h4 className="font-medium text-foreground flex items-center space-x-2">
          <Icon name="Bell" size={16} className="text-primary" />
          <span>ประกาศบริการ</span>
        </h4>
        
        {announcements?.length > 0 ? (
          <div className="space-y-3">
            {announcements?.map((announcement) => (
              <div key={announcement?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon 
                    name={announcement?.type === 'info' ? 'Info' : 'CheckCircle'} 
                    size={16} 
                    className={`mt-0.5 ${
                      announcement?.type === 'info' ? 'text-primary' : 'text-success'
                    }`}
                  />
                  <div className="flex-1">
                    <h5 className="font-medium text-foreground mb-1">
                      {announcement?.title}
                    </h5>
                    <p className="text-sm text-muted-foreground mb-2">
                      {announcement?.message}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {announcement?.date?.toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Bell" size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">ไม่มีประกาศในขณะนี้</p>
          </div>
        )}
      </div>
      {/* Emergency Contact */}
      <div className="mt-6 p-4 bg-error/5 border border-error/20 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Phone" size={16} className="text-error" />
          <span className="font-medium text-error">ติดต่อฉุกเฉิน</span>
        </div>
        <p className="text-sm text-muted-foreground">
          หากพบปัญหาเร่งด่วน กรุณาติดต่อ: <span className="font-medium text-foreground">02-123-4567 ต่อ 911</span>
        </p>
      </div>
    </div>
  );
};

export default ServiceStatus;